import { useMutation } from '@tanstack/react-query'

import type { TemplatePatch } from '@/lib/api/templates'
import { templatesApi } from '@/lib/api'
import { useCockpitContext } from '../useCockpit'

/**
 * One setting on the template, saved and then re-read.
 *
 * Every control on this tab writes through `PATCH /v1/templates/:id` and
 * then invalidates `cockpitKeys.template`, so the value on screen is always
 * the value the gateway kept — not the one the control sent. That matters
 * more here than anywhere else in the cockpit, because three of the keys in
 * `TemplatePatch` are **silently ignored** by the Postgres handler when they
 * are empty or null (see `SettingsTab.tsx`), and an optimistic update would
 * show a change the server never made.
 *
 * This build mounts no Toaster, so a failure is reported inline by
 * `<SaveHint>` next to the control that caused it.
 */
export function useTemplateSetting() {
  const cockpit = useCockpitContext()
  return useMutation({
    mutationFn: (patch: TemplatePatch) => templatesApi.patch(cockpit.templateId, patch),
    onSuccess: () => cockpit.invalidate.template(),
  })
}
